import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Wheat, Flame, Hand, Award, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO_IMAGE } from "@/data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 0.72, 0.21, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const BADGES = [
  "Ingrediente de calitate",
  "Pasiune pentru fiecare creație",
  "Experiență din 2019",
  "Coapte & congelate",
  "Beclean, Transilvania",
];

const STATS = [
  { value: "2019", label: "Anul în care a început povestea" },
  { value: "20+", label: "Sortimente premium" },
  { value: "HoReCa", label: "Retail & distribuție" },
  { value: "100%", label: "Calitate constantă" },
];

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="DOTAPAN — făină, unt, aluat, croissante"
          className="w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute inset-0 grain" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto container-px py-28 md:py-40">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl text-[#f6efe0]">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/40 bg-black/30 backdrop-blur">
            <Wheat className="h-3.5 w-3.5 text-gold" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-gold">Beclean • Transilvania • Din 2019</span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="h-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white"
          >
            Din inima Transilvaniei, <br />
            <span className="shimmer-text italic font-display">pentru bucătării</span> <br />
            care nu fac compromisuri.
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-6 text-base md:text-lg text-white/80 max-w-xl leading-relaxed">
            Produse coapte și congelate, create în Beclean cu rețete autentice, ingrediente alese și respect pentru
            meserie. Pentru HoReCa, retail și distribuitori care vor constanță reală.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" data-testid="hero-cta-primary-link">
              <Button className="btn-gold rounded-full h-12 px-7 text-sm font-semibold tracking-wide" data-testid="hero-cta-primary">
                Cere ofertă HoReCa <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/produse" data-testid="hero-cta-secondary-link">
              <Button variant="outline" className="rounded-full h-12 px-7 text-sm border-white/30 text-white hover:border-gold hover:text-gold bg-white/5 backdrop-blur" data-testid="hero-cta-secondary">
                Vezi produsele
              </Button>
            </Link>
          </motion.div>

          <motion.p variants={fadeUp} className="mt-5 text-xs text-white/60">
            Croissante • Foietaje • Blaturi • Fursecuri • Saleuri • Produse gata de lucru pentru afacerea ta.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-2.5">
            {BADGES.map((b, i) => (
              <span key={i} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] rounded-full border border-white/20 text-white/80 bg-white/5 backdrop-blur">
                {b}
              </span>
            ))}
          </motion.div>
        </motion.div>

        {/* Inovație teaser banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-16 md:mt-20"
        >
          <Link to="/inovatie" data-testid="hero-inovatie-banner" className="group block">
            <div className="relative overflow-hidden rounded-2xl border border-[#8B0000]/40 bg-gradient-to-r from-[#1a0000] via-[#8B0000]/30 to-[#0b0b0f] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 backdrop-blur transition-all hover:border-[#C9A45C]/60 hover:shadow-[0_25px_60px_-15px_rgba(139,0,0,0.6)]">
              <div className="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none bg-[radial-gradient(circle_at_right,rgba(201,164,92,0.35),transparent_60%)]" />
              <div className="relative flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8B0000] text-white text-[10px] font-bold uppercase tracking-[0.25em] shadow-[0_0_20px_rgba(139,0,0,0.6)]">
                  <Sparkles className="h-3 w-3" /> Nou 2026
                </span>
              </div>
              <div className="relative flex-1">
                <div className="font-display text-xl md:text-2xl text-white leading-tight">
                  <span className="text-[#C9A45C]">Black Velvet</span> & <span className="text-[#ff5c5c]">Dracula&apos;s Favorite</span> Croissant
                </div>
                <div className="mt-1 text-sm text-white/70">
                  Două produse iconice pentru HoReCa premium, retail și zone turistice.
                </div>
              </div>
              <div className="relative inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-b from-[#8B0000] to-[#5c0000] text-white text-sm font-semibold shadow-[0_15px_40px_-10px_rgba(139,0,0,0.7)] group-hover:brightness-110 group-hover:translate-x-1 transition-all">
                Cere ofertă Inovație <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur"
        >
          {STATS.map((s, i) => (
            <div key={i} className="p-6 md:p-8 bg-black/30">
              <div className="font-display text-3xl md:text-4xl text-gold">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.2em] text-white/60">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/60 z-10">
        <span className="text-[10px] uppercase tracking-[0.3em]">scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce text-gold" />
      </div>
    </section>
  );
}
